import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomFileUpload from "../../elements/CustomFileUpload";
import {
  setGlobalLoader,
  showSnackbar,
} from "../../redux/actions/commonAction";
import { getAllCredentials } from "../../redux/actions/credentialAction";
import {
  downloadFile,
  getAutomationAssessment,
  getRegionsByAccount,
} from "../../redux/actions/stateFileAction";
import Types from "../../redux/types";
import { debounce, validateStatefilePath } from "./../../utils/helpers";
import CustomAssessmentReport from "./reports/assessmentReport";
import { useStyles } from "./styles";

const AssessmentDetails = () => {
  const dispatch = useDispatch();
  const { allCredentials } = useSelector((state) => state.credential);
  const { automationAssessment, allRegions } = useSelector(
    (state) => state.stateFile
  );
  const classes = useStyles();

  const [urlError, setUrlError] = useState("");
  const [report, setReport] = useState(false);
  const [fileType, setFileType] = useState("url");
  const [state, setState] = useState({
    credential: null,
    region: "",
    url: "",
    file: null,
  });
  const [dropDownLoading, setDropDownLoading] = useState(false);

  useEffect(() => {
    dispatch(getAllCredentials());
    dispatch(setGlobalLoader(true));
    dispatch({
      type: Types.REGIONS_BY_ACCOUNT,
      payload: [],
    });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (allCredentials.length > 0) {
      dispatch(setGlobalLoader(false));
    }
    //eslint-disable-next-line
  }, [allCredentials]);

  const debouncedValidation = debounce((val) => {
    if (val?.length > 0 && !validateStatefilePath(val)) {
      setUrlError("Invalid S3 Path");
    } else {
      setUrlError("");
    }
  }, 500);

  const updateLocalState = (key, value = "") => {
    let newKeys = {};
    if (key === "credential") {
      newKeys["region"] = "";
      dispatch({
        type: Types.REGIONS_BY_ACCOUNT,
        payload: [],
      });
    }
    setState((prevState) => ({
      ...prevState,
      ...newKeys,
      [key]: value,
    }));
  };

  const handleChange = (key, value) => {
    updateLocalState(key, value);
    if (value) {
      if (key === "credential") {
        setDropDownLoading(true);
        dispatch(
          showSnackbar({
            message:
              "Retrieving the regions available for your account, please wait...",
            severity: "info",
            open: true,
          })
        );
        dispatch(
          getRegionsByAccount(value?.credName, () => setDropDownLoading(false))
        );
      }
    }
  };

  const handleSubmit = () => {
    const payload = {
      file_exists: fileType === "file",
      file: state.file,
      cred_name: state?.credential?.credName,
      region: state?.region?.region,
      s3_url: state?.url,
    };
    dispatch(
      getAutomationAssessment(payload, () => {
        setReport(true);
      })
    );
  };

  const handleDownload = async (callback) => {
    const payload = {
      bucketName: automationAssessment?.s3?.bucketName,
      key: automationAssessment?.s3?.key,
      region: automationAssessment?.s3?.region,
      credentials: automationAssessment?.s3?.credName,
    };
    await dispatch(downloadFile(payload, payload.key.split("/")[1], callback));
  };

  const onReportClose = () => {
    setState({
      credential: null,
      region: "",
      url: "",
    });
    setReport(false);
  };

  return (
    <Box>
      <Box width={"100%"} display={"flex"} justifyContent={"space-between"}>
        <Typography variant="h5" gutterBottom={true}>
          Automation Assessment
        </Typography>
      </Box>
      {!report ? (
        <>
          <Box className={classes.selectView}>
            <Autocomplete
              size="small"
              options={allCredentials}
              value={state.credential}
              onChange={(e, value) => {
                handleChange("credential", value);
              }}
              sx={{ width: 300 }}
              getOptionLabel={(option) => option.accountNumber}
              renderInput={(params) => (
                <TextField {...params} label="Accounts" />
              )}
            />
            <Autocomplete
              size="small"
              disabled={!(allRegions?.length > 0)}
              options={allRegions}
              value={state.region}
              onChange={(e, value) => {
                handleChange("region", value);
              }}
              sx={{ width: 300 }}
              getOptionLabel={(option) => option?.region || ""}
              renderInput={(params) => <TextField {...params} label="Region" />}
            />
            {dropDownLoading && (
              <CircularProgress className={classes.loader} size={30} />
            )}
          </Box>
          <Typography variant="subtitle2" gutterBottom={true}>
            State file
          </Typography>
          <RadioGroup
            row
            value={fileType}
            onChange={(e) => setFileType(e.target.value)}
          >
            <FormControlLabel
              labelPlacement="start"
              value="url"
              control={<Radio />}
              label="S3 bucket path"
            />
            <FormControlLabel
              labelPlacement="start"
              value="file"
              control={<Radio />}
              label="Upload state file"
            />
          </RadioGroup>
          {fileType === "file" ? (
            <Box my={2}>
              <CustomFileUpload
                title="Upload state file"
                value={state.file}
                onChange={(file) => handleChange("file", file)}
              />
            </Box>
          ) : (
            <TextField
              size="small"
              fullWidth
              required
              value={state.url}
              onChange={(e) => {
                handleChange("url", e.target.value);
                debouncedValidation(e.target.value);
              }}
              label="S3 Bucket Path"
              multiline
              margin="normal"
              error={Boolean(urlError)}
              helperText={urlError}
              maxRows={2}
            />
          )}
          <Box className={classes.footerView}>
            <Button
              disabled={
                !(
                  state.credential &&
                  state.region &&
                  (fileType === "file"
                    ? state.file
                    : state.url?.length > 0 && !urlError)
                )
              }
              onClick={handleSubmit}
              variant="contained"
            >
              Submit
            </Button>
          </Box>
        </>
      ) : (
        <CustomAssessmentReport
          state={{ ...state, fileType }}
          data={automationAssessment}
          onClose={onReportClose}
          handleDownload={handleDownload}
        />
      )}
    </Box>
  );
};

export default AssessmentDetails;
